import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, BellRing, Check, X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNotifications } from '@/hooks/useNotifications';
import { useAuth } from '@/context/AuthContext';

const NotificationBell = () => {
  const { user } = useAuth();
  const {
    isSupported,
    permission,
    isEnabled,
    hasAccess,
    newQuotesCount,
    pendingQuotesCount,
    requestPermission,
    clearNewQuotes,
    disableNotifications
  } = useNotifications(user?.role);

  const [isOpen, setIsOpen] = useState(false);

  // Don't render if user doesn't have admin/employee access
  if (!hasAccess) return null;

  const handleEnableNotifications = async () => {
    await requestPermission();
  };

  const handleOpenAdmin = () => {
    clearNewQuotes();
    window.location.href = '/admin';
  };

  // Always show a badge with pending quotes count
  const badgeCount = newQuotesCount > 0 ? newQuotesCount : pendingQuotesCount;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-steel-300 hover:text-white hover:bg-navy-700"
          data-testid="notification-bell"
        >
          {isEnabled && newQuotesCount > 0 ? (
            <motion.div
              animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
            >
              <BellRing className="w-5 h-5" />
            </motion.div>
          ) : (
            <Bell className="w-5 h-5" />
          )}
          
          {/* Notification badge - always show pending count */}
          <AnimatePresence>
            {badgeCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className={`absolute -top-1 -right-1 w-5 h-5 text-white text-xs font-bold rounded-full flex items-center justify-center ${
                  newQuotesCount > 0 ? 'bg-red-500' : 'bg-gold'
                }`}
              >
                {badgeCount > 9 ? '9+' : badgeCount}
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b border-steel-200">
          <h3 className="font-semibold text-navy-800 flex items-center gap-2">
            <Bell className="w-4 h-4 text-gold" />
            Quote Notifications
          </h3>
        </div>
        
        <div className="p-4">
          {/* Always show pending quotes summary */}
          {pendingQuotesCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gold/10 border border-gold/30 rounded-lg p-3 mb-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-gold" />
                  <div>
                    <p className="font-semibold text-navy-800">
                      {pendingQuotesCount} Pending Quote{pendingQuotesCount > 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-steel-500">
                      Waiting for review
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={handleOpenAdmin}
                  className="btn-accent"
                >
                  View
                </Button>
              </div>
            </motion.div>
          )}

          {/* Notification Content */}
          {renderNotificationContent()}
        </div>
        
        <div className="p-3 bg-steel-50 border-t border-steel-200">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenAdmin}
            className="w-full text-sm"
          >
            Open Admin Dashboard
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
  
  // Helper function to render notification content based on state
  function renderNotificationContent() {
    // Not enabled but browser supports notifications
    if (!isEnabled && isSupported) {
      return (
        <div className="text-center">
          <BellOff className="w-10 h-10 text-steel-300 mx-auto mb-3" />
          <p className="text-sm text-steel-600 mb-4">
            Enable push notifications for instant alerts when new quotes arrive.
          </p>
          {permission === 'denied' ? (
            <p className="text-xs text-red-500 mb-2">
              Notifications blocked. Enable them in browser settings.
            </p>
          ) : (
            <Button 
              onClick={handleEnableNotifications}
              variant="outline"
              className="w-full"
            >
              <Bell className="w-4 h-4 mr-2" />
              Enable Push Notifications
            </Button>
          )}
        </div>
      );
    }
    
    // Notifications enabled
    if (isEnabled) {
      return (
        <div className="space-y-3">
          {pendingQuotesCount === 0 && (
            <div className="text-center py-2">
              <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-steel-600">
                All caught up! No pending quotes.
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between pt-2 border-t border-steel-100">
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Check className="w-3 h-3" />
              Push notifications enabled
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={disableNotifications}
              className="text-xs text-steel-500 hover:text-red-500"
            >
              <X className="w-3 h-3 mr-1" />
              Disable
            </Button>
          </div>
        </div>
      );
    }
    
    // Default: no pending quotes
    if (pendingQuotesCount === 0) {
      return (
        <div className="text-center py-2">
          <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-sm text-steel-600">
            No pending quotes at the moment.
          </p>
        </div>
      );
    }
    
    return null;
  }
};

export default NotificationBell;
